/**
 * ChargeNest — Finanz-Rechenkern (Single Source of Truth)
 * =====================================================
 * ALLE Auswertungen der App (Dashboard, Umsätze, Steuerbericht, Kundenübersicht,
 * Provisionsabrechnung, Auszahlungs-Abgleich) rechnen ÜBER DIESE DATEI.
 * Damit kann es keine Seite mehr geben, die Gutschriften anders behandelt als
 * eine andere.
 *
 * ── FACHLOGIK (verbindlich, bestätigt durch Auto-Sync v2 / Rechargy-API) ──
 *
 * 1. Brutto
 *    Der erfasste Betrag ist BRUTTO (inkl. MwSt).
 *      netto = brutto / (1 + mwStSatz/100)
 *      mwSt  = brutto − netto
 *
 * 2. Anteil/Abzug (der "25 %"-Vertrag mit Rechargy)
 *    Rechargy liefert pro Bestellung den Settlement-Brutto (`settlementAmount`)
 *    und unseren vertraglichen Anteil in % (`deductionRate`, i.d.R. 25).
 *      anteilBrutto  = settlementAmount × (100 − deductionRate) / 100
 *      abzugBetrag   = settlementAmount − anteilBrutto
 *    `revenue_amount` IST der Anteilbrutto (unser vertraglicher Anteil).
 *    ==> Der Abzug darf NIE ein zweites Mal auf revenue_amount angewendet werden.
 *
 * 3. Provision
 *    Die Provision wird vom BRUTTO (Anteilbrutto) berechnet — Basis ist der
 *    Anteilbrutto, exakt so wie der Auto-Sync es schreibt.
 *      provision = anteilBrutto × provisionsSatz/100
 *    Für historische Buchungen gilt IMMER der beim Buchen gespeicherte Satz
 *    (`commission_rate_used`) — NIE der aktuelle Kundensatz. Sonst ändern sich
 *    historische Provisionen, sobald ein Satz geändert wird.
 *
 * 4. Unser Anteil
 *      ourShare = netto − provision
 *
 * 5. Auszahlung
 *      erwarteteAuszahlung = provision   (was dem Kunden auszuzahlen ist)
 *      tatsaechlicheAuszahlung = transferred_amount
 *      auszahlungsDifferenz  = erwarteteAuszahlung − tatsaechlicheAuszahlung
 *
 * 6. Steuerrelevanz
 *      STEUERWIRKSAM  : Gutschriften (expenses.type === 'credit')  → Umsatzsteuer
 *      VORSTEUER      : Ausgaben     (expenses.type === 'expense') → Vorsteuer
 *      ZAHLUNG        : expenses.type === 'tax_payment*'            → bereits bezahlt
 *      REFERENZ       : credits.transaction_type === 'revenue'     → Tagesumsatz je Station.
 *                        Wird in KEINER steuerlichen Kennzahl mitgezählt, dient aber
 *                        der Provision und der Auszahlungsplanung.
 */

export const DEFAULT_VAT_RATE = 19;
export const DEFAULT_COMMISSION_RATE = 10;
/** Rechargy-Anteilabzug vom Settlement-Brutto (i.d.R. 25 %) */
export const DEFAULT_DEDUCTION_RATE = 25;

const num = (value, fallback = 0) => {
  const parsed = parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const round2 = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;

/**
 * Anlagevermögen (Investition) — z. B. gekaufte Powerbank-Stationen.
 * Kein Betriebsaufwand: mindert den Gewinn nicht (Abschreibung über die
 * Nutzungsdauer) und die Vorsteuer daraus wird nach §15a UStG über die
 * Nutzungsdauer erstattet. Deshalb getrennt von den Ausgaben ausweisen.
 */
export const isAssetType = (expense) =>
  ['asset', 'investment', 'anlage', 'anlagevermoegen'].includes(String(expense?.type || '').toLowerCase());

/**
 * Nutzungsdauer für Anlagevermögen (Stationen, Werkzeuge) in Jahren.
 * Linear, Restwert 0. Anlagevermögen mindert den Gewinn nicht im Anschaffungsjahr,
 * sondern über die Abschreibung – ohne sie wäre der Gewinn massiv überhöht.
 * Der Wert ist eine Schätzung und hier zentral änderbar.
 */
export const ASSET_USEFUL_LIFE_YEARS = 7;

/**
 * Kumulierte Abschreibung eines Anlagevermögen-Postens vom Anschaffungsdatum
 * bis zum Stichtag (linear, zeitanteilig, Restwert 0).
 */
export function assetDepreciation(asset, asOf = new Date()) {
  const gross = num(asset?.amount, 0);
  if (!gross) return 0;
  const net = asset.net_amount !== undefined && asset.net_amount !== null
    ? num(asset.net_amount)
    : gross;
  const start = new Date(asset.date);
  if (Number.isNaN(start.getTime())) return 0;
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + ASSET_USEFUL_LIFE_YEARS);
  const stop = new Date(asOf.getTime());
  if (stop > end) stop.setTime(end.getTime());
  if (stop <= start) return 0;
  const months = (stop.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30.4375);
  return round2((net / ASSET_USEFUL_LIFE_YEARS) * (months / 12));
}

/**
 * Vollständige Buchungsrechnung.
 * @param {object} input
 * @param {number} [input.gross]              Bruttobetrag der Buchung (Anteilbrutto)
 * @param {number} [input.vatRate]          MwSt-Satz in %
 * @param {number} [input.commissionRate]   Provisionssatz in % (der GESPEICHERTE Satz)
 * @param {number} [input.settlementAmount] Settlement-Brutto vor Abzug (falls bekannt)
 * @param {number} [input.deductionRate]    Abzugsrate in % (i.d.R. 25)
 * @param {number} [input.transferredAmount] tatsächlich ausgezahlter Betrag
 */
export const calculateBooking = ({
  gross = 0,
  vatRate = DEFAULT_VAT_RATE,
  commissionRate = DEFAULT_COMMISSION_RATE,
  settlementAmount = null,
  deductionRate = DEFAULT_DEDUCTION_RATE,
  transferredAmount = 0,
} = {}) => {
  const revenueGross = round2(gross);
  const vat = num(vatRate, DEFAULT_VAT_RATE);
  const rate = num(commissionRate, 0);
  const deductedRate = num(deductionRate, DEFAULT_DEDUCTION_RATE);

  const net = round2(vat > 0 ? revenueGross / (1 + vat / 100) : revenueGross);
  const vatAmount = round2(revenueGross - net);

  const commissionAmount = round2((revenueGross * rate) / 100);
  const franchiseAmount = commissionAmount;
  const ourShare = round2(net - commissionAmount);

  // Settlement / Abzug
  const hasSettlement = settlementAmount !== null && settlementAmount !== undefined && num(settlementAmount) > 0;
  const settlement = hasSettlement ? round2(settlementAmount) : null;
  const effectiveDeductionRate = hasSettlement
    ? round2((deductedRate * 100) / 100)
    : deductedRate;
  const deductionAmount = hasSettlement ? round2(settlement - revenueGross) : round2((revenueGross * deductedRate) / 100);

  const expectedPayout = commissionAmount;
  const actualPayout = round2(transferredAmount);
  const payoutDifference = round2(expectedPayout - actualPayout);

  return {
    gross: revenueGross,
    net,
    vatRate: vat,
    vatAmount,
    commissionRate: rate,
    commissionAmount,
    franchiseAmount,
    ourShare,
    settlementAmount: settlement,
    deductionRate: effectiveDeductionRate,
    deductionAmount,
    expectedPayout,
    actualPayout,
    payoutDifference,
  };
};

/**
 * Ein gespeichertes credit-Dokument in eine vollständige Buchungsrechnung überführen.
 * Berücksichtigt rückwirkend ergänzte Felder (settlement_amount etc.).
 */
export const bookingFromDocument = (doc = {}) => {
  const revenueGross = num(doc.revenue_amount ?? doc.amount, 0);
  const stored = doc.settlement_amount !== undefined && doc.settlement_amount !== null && doc.settlement_amount !== 0
    ? num(doc.settlement_amount)
    : null;
  const storedDeductionRate = doc.deduction_rate !== undefined && doc.deduction_rate !== null
    ? num(doc.deduction_rate, DEFAULT_DEDUCTION_RATE)
    : DEFAULT_DEDUCTION_RATE;

  const calc = calculateBooking({
    gross: revenueGross,
    vatRate: doc.vat_rate !== undefined && doc.vat_rate !== null ? num(doc.vat_rate) : DEFAULT_VAT_RATE,
    // WICHTIG: gespeicherter Satz der Buchung, NICHT der aktuelle Kundensatz
    commissionRate: doc.commission_rate_used !== undefined && doc.commission_rate_used !== null
      ? num(doc.commission_rate_used)
      : 0,
    settlementAmount: stored,
    deductionRate: storedDeductionRate,
    transferredAmount: doc.transferred_amount,
  });

  return {
    ...calc,
    id: doc.$id || doc.id,
    customerId: doc.customer_id,
    transactionType: doc.transaction_type || 'revenue',
    description: doc.description || '',
    date: doc.transfer_date || doc.period_start || doc.date || '',
    periodStart: doc.period_start || doc.transfer_date || '',
    periodEnd: doc.period_end || doc.transfer_date || '',
    isRevenue: (doc.transaction_type || 'revenue') === 'revenue',
    isPayout: doc.transaction_type === 'transfer',
  };
};

/**
 * Aggregation über alle Buchungen eines Kunden.
 * Provision wird je Buchung mit dem GESPEICHERTEN Satz gerechnet.
 * @param {Record<string, any>} customer  Kunden-Dokument (id/$id, name, ...)
 * @param {Array<Record<string, any>>} credits  Buchungen
 * @returns {Record<string, any>}  Summen je Kunde (inkl. Original-Feldern des Kunden)
 */
export const customerTotals = (customer = /** @type {Record<string, any>} */ ({}), credits = []) => {
  const bookings = credits
    .filter((credit) => credit.customer_id === customer.$id || credit.customer_id === customer.id)
    .map(bookingFromDocument);

  const revenueBookings = bookings.filter((booking) => booking.isRevenue);
  const payoutBookings = bookings.filter((booking) => booking.isPayout);

  const totalRevenue = round2(revenueBookings.reduce((sum, b) => sum + b.gross, 0));
  const totalNet = round2(revenueBookings.reduce((sum, b) => sum + b.net, 0));
  const totalVat = round2(revenueBookings.reduce((sum, b) => sum + b.vatAmount, 0));
  const totalCommission = round2(revenueBookings.reduce((sum, b) => sum + b.commissionAmount, 0));
  const totalOurShare = round2(revenueBookings.reduce((sum, b) => sum + b.ourShare, 0));
  const totalSettlement = round2(revenueBookings.reduce((sum, b) => sum + (b.settlementAmount || 0), 0));
  const totalDeduction = round2(revenueBookings.reduce((sum, b) => sum + b.deductionAmount, 0));
  const totalTransferred = round2(payoutBookings.reduce((sum, b) => sum + b.actualPayout, 0));

  return {
    ...customer,
    bookings,
    totalRevenue,
    totalNet,
    totalVat,
    totalCommission,
    totalOurShare,
    totalSettlement,
    totalDeduction,
    totalTransferred,
    /** noch ausstehende Auszahlung an den Kunden */
    outstanding: round2(totalCommission - totalTransferred),
  };
};

/**
 * Einheitliche Zeilen für den Steuerbericht.
 *
 * Steuerliche Zuordnung (verbindlich):
 *   - Gutschrift  (expenses.type === 'credit')  → steuerwirksamer Umsatz (Umsatzsteuer)
 *   - Ausgabe     (expenses.type === 'expense') → Vorsteuer
 *   - Steuerzahlung (expenses.type === 'tax_payment*') → bereits bezahlte MwSt
 *   - Umsatz-Credits (credits, transaction_type 'revenue') → REFERENZ, KEINE steuerliche Kennzahl
 *   - Auszahlungs-Credits (credits, transaction_type 'transfer') → durchlaufende Zahlung, keine Kennzahl
 */
export const taxReportRows = ({ credits = [], expenses = [], customers = [], year = 'all', quarter = 'all' } = {}) => {
  const rows = [];

  const inPeriod = (date) => {
    if (!date) return false;
    const value = String(date).slice(0, 10);
    if (year && year !== 'all' && value.slice(0, 4) !== String(year)) return false;
    if (quarter && quarter !== 'all') {
      const month = Number(value.slice(5, 7));
      if (Math.ceil(month / 3) !== Number(quarter)) return false;
    }
    return true;
  };

  // ── Referenz: Tagesumsätze je Station (KEINE steuerliche Kennzahl) ──
  credits.forEach((credit) => {
    if ((credit.transaction_type || 'revenue') === 'transfer') return;
    const booking = bookingFromDocument(credit);
    if (!inPeriod(booking.date)) return;
    const customer = customers.find((item) => item.$id === credit.customer_id || item.id === credit.customer_id);
    rows.push({
      id: `credit-${booking.id}`,
      date: booking.date,
      description: booking.description || 'Umsatz',
      source: customer?.name || 'Kundenumsatz',
      type: 'Geschäftsumsatz (Referenz)',
      gross: booking.gross,
      net: booking.net,
      vat: 0,
      vatRate: booking.vatRate,
      direction: 'reference',
      settlementAmount: booking.settlementAmount,
      deductionRate: booking.deductionRate,
      deductionAmount: booking.deductionAmount,
      expectedPayout: booking.expectedPayout,
      actualPayout: booking.actualPayout,
      payoutDifference: booking.payoutDifference,
    });
  });

  // ── Gutschriften / Ausgaben / Steuerzahlungen / Anlagevermögen ─────────
  expenses.forEach((expense) => {
    const date = expense.date || expense.transfer_date || '';
    if (!inPeriod(date)) return;
    const gross = num(expense.amount, 0);
    const vatRate = expense.vat_rate !== undefined && expense.vat_rate !== null ? num(expense.vat_rate) : 0;
    const net = expense.net_amount !== undefined && expense.net_amount !== null
      ? num(expense.net_amount)
      : (vatRate > 0 ? round2(gross / (1 + vatRate / 100)) : gross);
    const vat = expense.vat_amount !== undefined && expense.vat_amount !== null
      ? num(expense.vat_amount)
      : round2(gross - net);
    const type = String(expense.type || 'expense');
    const isCredit = ['credit', 'gutschrift', 'income', 'revenue', 'einnahme'].includes(type.toLowerCase());
    const isTaxPayment = type.startsWith('tax_payment');
    // ANLAGEVERMÖGEN (z. B. gekaufte Powerbank-Stationen) ist KEIN Betriebsaufwand:
    // Es mindert den Gewinn nicht (Abschreibung über die Nutzungsdauer) und die
    // Vorsteuer daraus wird nach §15a UStG über die Nutzungsdauer erstattet.
    // Deshalb eigene Richtung 'asset' – nicht in die Vorsteuer und nicht in den Gewinn.
    const isAsset = ['asset', 'investment', 'anlage', 'anlagevermoegen'].includes(type.toLowerCase());
    // Kunden-Auszahlungen (Überweisungen an Kunden) sind durchlaufende Zahlungen:
    // kein Betriebsaufwand, keine Vorsteuer – eigener Tab, eigene Kennzahl.
    const isTransfer = ['transfer', 'payout', 'auszahlung'].includes(type.toLowerCase());

    rows.push({
      id: `expense-${expense.$id || expense.id}`,
      date,
      description: expense.title || 'Transaktion',
      source: expense.category || 'Allgemein',
      type: isCredit ? 'Gutschrift' : isTaxPayment ? 'Steuerzahlung' : isAsset ? 'Anlagevermögen' : isTransfer ? 'Kunden-Auszahlung' : 'Ausgabe',
      gross,
      net,
      vat,
      vatRate,
      direction: isCredit ? 'output' : isTaxPayment ? 'payment' : isAsset ? 'asset' : isTransfer ? 'transfer' : 'input',
      taxKind: expense.type,
    });
  });

  rows.sort((a, b) => String(a.date).localeCompare(String(b.date)));

  const outputVat = round2(rows.filter((row) => row.direction === 'output').reduce((sum, row) => sum + row.vat, 0));
  const inputVat = round2(rows.filter((row) => row.direction === 'input').reduce((sum, row) => sum + row.vat, 0));
  // Vorsteuer aus Investitionen (§ 15 Abs. 1a UStG): wird getrennt ausgewiesen und NICHT
  // mit der laufenden Vorsteuer verrechnet – sie ist erstattungsfähig über die
  // Nutzungsdauer, mindert aber den Gewinn nicht (Abschreibung).
  const assetVat = round2(rows.filter((row) => row.direction === 'asset').reduce((sum, row) => sum + row.vat, 0));
  const assetGross = round2(rows.filter((row) => row.direction === 'asset').reduce((sum, row) => sum + row.gross, 0));
  const vatPaid = round2(
    rows.filter((row) => row.taxKind === 'tax_payment_vat' || row.taxKind === 'tax_payment')
      .reduce((sum, row) => sum + row.gross, 0)
  );
  const profitTaxPaid = round2(
    rows.filter((row) => row.taxKind === 'tax_payment_profit')
      .reduce((sum, row) => sum + row.gross, 0)
  );
  const referenceGross = round2(
    rows.filter((row) => row.direction === 'reference').reduce((sum, row) => sum + row.gross, 0)
  );

  return {
    rows,
    outputVat,
    inputVat,
    vatPaid,
    profitTaxPaid,
    vatLiability: round2(outputVat - inputVat),
    openVat: round2(outputVat - inputVat - vatPaid),
    referenceGross,
    assetVat,
    assetGross,
  };
};

export default {
  DEFAULT_VAT_RATE,
  DEFAULT_COMMISSION_RATE,
  DEFAULT_DEDUCTION_RATE,
  calculateBooking,
  bookingFromDocument,
  customerTotals,
  taxReportRows,
  round2,
};
